<template>
  <div>
    <v-container>
      <Breadcrumbs :breadcrumbs="breadcrumbs"/>
    </v-container>
    <v-container v-if="this.isLoading">
      <v-row class="d-flex justify-space-between">
        <v-col cols="4">
          <v-skeleton-loader type="heading"></v-skeleton-loader>
        </v-col>
        <v-col>
          <v-skeleton-loader type="chip"></v-skeleton-loader>
        </v-col>
        <v-col cols="2" class="d-flex justify-end">
          <v-skeleton-loader type="button"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6" class="pt-0">
          <v-skeleton-loader type="text"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row class="d-flex justify-end">
        <v-col cols="2" class="d-flex justify-end">
          <v-skeleton-loader type="button"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row class="d-flex justify-center">
        <v-col>
          <v-card shaped>
            <v-skeleton-loader type="table"></v-skeleton-loader>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <transition name="slade-fade-down-slow">
      <v-container v-if="!this.isLoading">
        <v-row class="d-flex align-center">
          <v-col cols="9">
            <h1>
              {{ this.curriculum.fields.Name }}
              <v-chip v-if="showChip(curriculum)">
                {{ getChipText(curriculum) }}
              </v-chip>
            </h1>
          </v-col>
          <v-col class="d-flex justify-end">
            <CurriculumForm
              :curriculum="curriculum"
              title="Edit Curriculum"
              icon="mdi-pencil"
              method="edit"
              :sessions="sessions"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0">
            <p>{{ this.curriculum.fields.Description }}</p>
          </v-col>
        </v-row>
        <v-row class="d-flex align-center">
          <v-col>
            <v-list rounded shaped :elevation="2">
              <v-list-item-group>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-link</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      Sign Up URL
                    </v-list-item-subtitle>
                    <v-list-item-title>
                      <a link :href="signUpUrl" target="_blank">
                        {{ signUpUrl }}
                      </a>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-col>
          <v-col class="d-flex justify-end">
            <SessionForm
              :curriculum="curriculum"
              title="Create Session"
              method="create"
              buttonColor="primary"
            />
          </v-col>
        </v-row>
        <v-tabs class="d-flex justify-end" v-model="tab">
          <v-tab>
            <v-icon left>mdi-view-list</v-icon> Sessions
          </v-tab>
          <v-tab>
            <v-icon left>mdi-account-multiple</v-icon> Attendees
          </v-tab>
          <v-tab>
            <v-icon left>mdi-account-multiple-outline</v-icon> Not Attended
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item rounded>
            <v-row class="d-flex justify-center">
              <v-col>
                <SessionTable 
                  :curriculum="curriculum"
                  :sessions="sessions"
                  :loading="loadingSessions"
                />
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item rounded>
            <v-row class="d-flex justify-center">
              <v-col>
                <!-- this will split the table by session -->
                <CurriculumAttendees 
                  :sessions="sessions"
                  :loading="loadingSessions"
                  style="display: none;"
                />
                <!-- this will allow people to download reports -->
                <EmployeeTable 
                  :employees="allEmployeesSignedUp"
                  title="Employees Signed Up"
                  subtitle="This list contains all employees who have signed up for a session for this curriculum. Use the Checked In status to determine if they actually attended."
                  :headerProps="[
                    { text: 'Checked In', align: 'start', sortable: true, value: 'fields.Checked In'},
                    { text: 'Session', align: 'start', sortable: true, value: 'fields.Sessions Signed Up'},
                    { text: 'Email', align: 'start', sortable: true, value: 'fields.Email' },
                    { text: 'Title', align: 'start', sortable: true, value: 'fields.Title' },
                    { text: 'Department', align: 'start', sortable: true, value: 'fields.Department' },
                    { text: 'Title', align: 'start', sortable: true, value: 'fields.Location Formatted' },
                    { text: 'Position ID', align: 'start', sortable: true, value: 'fields.Position ID' },
                    { text: 'Hire Date', align: 'start', sortable: true, value: 'fields.Hire Date' },
                    { text: 'Is Manager', align: 'start', sortable: true, value: 'fields.Manager Position' },
                  ]"
                />
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item rounded>
            <v-row class="d-flex justify-center">
              <v-col>
                <EmployeeTable 
                  :employees="activeEmployeesNotAttended"
                  title="Employees Who Have Not Attended"
                  subtitle="This list contains active employees who have not been checked into a session for this curriculum. Employees may have signed up for sessions but may not have attended. The session an employee signed up for might be scheduled in the future."
                  :headerProps="[
                    { text: 'Session', align: 'start', sortable: true, value: 'fields.Sessions Signed Up'},
                    { text: 'Has Future Session', align: 'start', sortable: true, value: 'fields.Is Signed Up For Future Session'},
                    { text: 'Email', align: 'start', sortable: true, value: 'fields.Email' },
                    { text: 'Title', align: 'start', sortable: true, value: 'fields.Title' },
                    { text: 'Department', align: 'start', sortable: true, value: 'fields.Department' },
                    { text: 'Title', align: 'start', sortable: true, value: 'fields.Location Formatted' },
                    { text: 'Position ID', align: 'start', sortable: true, value: 'fields.Position ID' },
                    { text: 'Hire Date', align: 'start', sortable: true, value: 'fields.Hire Date' },
                    { text: 'Is Manager', align: 'start', sortable: true, value: 'fields.Manager Position' },
                  ]"
                />
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-container>
    </transition>
  </div>
</template>

<script>
  import Breadcrumbs from '@/components/Breadcrumbs'
  import CurriculumAttendees from '@/components/curriculum/Attendees'
  import { CurriculumChip } from '@/mixins/CurriculumChip.js'
  import CurriculumForm from '@/components/curriculum/Form'
  import EmployeeTable from '@/components/employee/Table'
  import SessionForm from '@/components/session/Form'
  import SessionTable from '@/components/session/Table'
  
  export default {
    name: 'Curriculum',
    components: {
      Breadcrumbs,
      CurriculumAttendees,
      CurriculumForm,
      EmployeeTable,
      SessionForm,
      SessionTable
    },
    mixins: [CurriculumChip],
    data () {
      return {
        loadingCurriculums: true,
        loadingSessions: true,
        tab: null
      }
    },
    created () {
      this.$store.dispatch('getRecords', {
        table: 'Curriculums',
        view: 'All Curriculums',
        dispatch: 'setCurriculums'
      }).then(() => {
        this.loadingCurriculums = false
      })
      this.$store.dispatch('getRecords', {
        table: 'Sessions',
        view: 'All Sessions',
        dispatch: 'setSessions'
      }).then(() => {
        this.loadingSessions = false
      })
      if (this.$store.getters.getEmployees.length === 0) {
        this.$store.dispatch('getRecords', {
          table: 'Employees',
          view: 'All Employees',
          dispatch: 'setEmployees'
        })
      } 
    },
    computed: {
      breadcrumbs () {
        return [
          {
            text: 'Curriculums',
            disabled: false,
            to: '/'
          },
          {
            text: this.curriculum ? this.curriculum.fields.Name : 'Loading...',
            disabled: true
          }
        ]
      },
      curriculum () {
        return this.$store.getters.getCurriculums.find(c => {
          return c.id === this.$route.params.id
        })
      },
      activeEmployeesNotAttended () {
        return this.$store.getters.getEmployees.filter(e => {
          return (!e.fields['Curriculums Checked In'] || !e.fields['Curriculums Checked In'].includes(this.curriculum.id)) &&
                 e.fields.Status !== 'Inactive'
        }).map(e => {
          e.fields['Sessions Signed Up'] = this.sessions.filter(s => {
            return e.fields['Sessions'] && e.fields['Sessions'].includes(s.id) && s.fields['Curriculum'].includes(this.curriculum.id)
          }).map(s => {
            let startDate = s.fields['Start']
            const dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric' }
            startDate ? startDate = new Date(startDate).toLocaleDateString('en-US', dateOptions) : 'No Date'
            return `${startDate} - ${s.fields['Name']}`
          }).join(', ')

          e.fields['Is Signed Up For Future Session'] = this.sessions.filter(s => {
            return e.fields['Sessions'] && e.fields['Sessions'].includes(s.id) &&
                   s.fields['Curriculum'] && s.fields['Curriculum'].includes(this.curriculum.id)
          }).map(s => {
            const startDate = new Date(s.fields['Start'])
            const now = new Date()
            return now.getTime() <= startDate.getTime()
          }).some(bool => bool === true)
          return e
        })
      },
      allEmployeesSignedUp () {
        return this.$store.getters.getEmployees.filter(e => {
          return (e.fields['Curriculums'] && e.fields['Curriculums'].includes(this.curriculum.id))
        }).map(e => {
          e.fields['Checked In'] = e.fields['Curriculums Checked In'] && e.fields['Curriculums Checked In'].includes(this.curriculum.id)
          e.fields['Checked In'] ? e.fields['Checked In'] = true : e.fields['Checked In'] = false
          e.fields['Sessions Signed Up'] = this.sessions.filter(s => {
            return e.fields['Sessions'] && e.fields['Sessions'].includes(s.id) && s.fields['Curriculum'].includes(this.curriculum.id)
          }).map(s => {
            let startDate = s.fields['Start']
            const dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric' }
            startDate ? startDate = new Date(startDate).toLocaleDateString('en-US', dateOptions) : 'No Date'
            return `${startDate} - ${s.fields['Name']}`
          })
          .join(', ')
          return e
        }).sort((a, b) => {
          return a.checkedIn > b.checkedIn ? -1 : 1
        })
      },
      isLoading () {
        return this.loadingCurriculums || this.loadingSessions
      },
      sessions () {
        return this.$store.getters.getSessions.slice(0).filter(s => {
          return s.fields.Curriculum.includes(this.curriculum.id)
        }).sort((a, b) => {
          return a.fields.Start < b.fields.Start ? 1 : -1
        })
      },
      signUpUrl () {
        return `${window.location.origin}/training/${this.curriculum.fields.URL}`
      },
    },
  }
</script>